import { OAuth2, OAuth2CreateOptions } from '@backstage/core-app-api';
import { SignInPage } from '@backstage/core-components';
import {
  BackstageIdentityApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import {
  ApiBlueprint,
  configApiRef,
  createApiRef,
  createFrontendModule,
  discoveryApiRef,
  oauthRequestApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import {
  SignInPageBlueprint,
  SignInPageProps,
} from '@backstage/plugin-app-react';

const gameplayAiOidcApiRef = createApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>().with({ id: 'auth.gameplayai-oidc' });

export function createGameplayAiOidcApi(
  options: Required<
    Pick<OAuth2CreateOptions, 'discoveryApi' | 'oauthRequestApi' | 'configApi'>
  >,
) {
  const { discoveryApi, oauthRequestApi, configApi } = options;
  return OAuth2.create({
    configApi,
    discoveryApi,
    oauthRequestApi,
    environment: configApi.getOptionalString('auth.environment'),
    provider: { id: 'oidc', title: 'Company SSO', icon: () => null },
    defaultScopes: ['openid', 'profile', 'email'],
  });
}

const gameplayAiOidcApi = ApiBlueprint.make({
  name: 'gameplayai-oidc',
  params: defineParams =>
    defineParams({
      api: gameplayAiOidcApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: createGameplayAiOidcApi,
    }),
});

export const GameplayAiSignInPage = (props: SignInPageProps) => {
  const configApi = useApi(configApiRef);
  if (configApi.getString('auth.environment') === 'development') {
    return <SignInPage {...props} providers={['guest']} />;
  }
  return (
    <SignInPage
      {...props}
      provider={{
        id: 'gameplayai-oidc-provider',
        title: 'Company SSO',
        message: 'Sign in with the organization identity provider',
        apiRef: gameplayAiOidcApiRef,
      }}
    />
  );
};

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => GameplayAiSignInPage,
  },
});

export const authenticationModule = createFrontendModule({
  pluginId: 'app',
  extensions: [gameplayAiOidcApi, signInPage],
});
